import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { userService } from "../services/userService";
import { Clock, Link21, Send2, TickCircle } from "iconsax-reactjs";

const Chat = ({ clientId, lawyerId, lbaId, clientName }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [userRole, setUserRole] = useState(null);

  const socketRef = useRef();
  const messagesEndRef = useRef(null);

  // ✅ Detect logged-in user
  useEffect(() => {
    const user = userService.getCurrentUser();

    if (user?.role === "lawyer" || user?.id === lawyerId) {
      setUserRole("lawyer");
    } else if (user?.companyId === clientId) {
      setUserRole("client");
    }
  }, [lawyerId, clientId]);

  // ✅ Socket setup
  useEffect(() => {
    socketRef.current = io(process.env.REACT_APP_API_URL, {
      withCredentials: true,
    });

    socketRef.current.emit("join_room", { lbaId });

    socketRef.current.on("chat_history", (history) => {
      setMessages(history || []);
    });

    socketRef.current.on("receive_message", (message) => {
      setMessages((prev) => [...prev, message]);
    });

    return () => socketRef.current.disconnect();
  }, [lbaId]);

  // ✅ Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ✅ Ownership check
  const isOwnMessage = (msg) => {
    if (userRole === "lawyer") return msg.senderId === lawyerId;
    if (userRole === "client") return msg.senderId === clientId;
    return false;
  };

  // ✅ Initials (Lawyer = L, Client = real initials)
  const getInitials = (msg) => {
    if (msg.senderRole === "lawyer") return "L";
    if (!clientName) return "C";
    const parts = clientName.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return parts[0][0].toUpperCase() + parts[1][0].toUpperCase();
  };

  // ✅ Send Message
  const sendMessage = () => {
    if (!input.trim()) return;

    const messageData = {
      lbaId,
      senderId: userRole === "lawyer" ? lawyerId : clientId,
      senderRole: userRole,
      content: input.trim(),
    };

    socketRef.current.emit("send_message", messageData);
    setInput("");
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDisplayDate = (date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const messageDate = new Date(date);

    if (messageDate.toDateString() === today.toDateString()) return "Today";
    if (messageDate.toDateString() === yesterday.toDateString())
      return "Yesterday";

    return messageDate.toLocaleDateString();
  };

  const getMessagesByDate = () => {
    const groups = [];
    let currentGroup = null;

    messages.forEach((msg) => {
      const messageDate = new Date(
        msg.timestamp || msg.createdAt
      ).toDateString();

      if (!currentGroup || currentGroup.date !== messageDate) {
        currentGroup = {
          date: messageDate,
          displayDate: getDisplayDate(msg.timestamp || msg.createdAt),
          messages: [],
        };
        groups.push(currentGroup);
      }

      currentGroup.messages.push(msg);
    });

    return groups;
  };

  const dateGroups = getMessagesByDate();

  return (
    <div className="w-full h-[650px] shadow rounded-3xs bg-white border border-gray-200 flex flex-col p-6 gap-6">

      {/* ✅ Header */}
      <div className="border-b pb-5 flex items-center gap-3">
        <div className="flex-1 flex flex-col gap-1">
          <div className="font-semibold text-lg">Live Chat</div>
          <div className="flex items-center gap-1 text-sm">
            <div className="h-2 w-2 rounded-full bg-green-500" />
            <span>Online</span>
          </div>
        </div>
      </div>

      {/* ✅ Messages */}
      <div className="flex-1 overflow-y-auto flex flex-col gap-8">

        {dateGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="flex flex-col gap-6">

            <div className="flex justify-center">
              <div className="rounded-full bg-neutrals-off-white px-3 py-1 text-xs">
                {group.displayDate}
              </div>
            </div>

            {group.messages.map((msg, msgIndex) => {
              const isOwn = isOwnMessage(msg);
              const initials = getInitials(msg);

              return (
                <div
                  key={msg._id || msgIndex}
                  className={`self-stretch flex items-end gap-3 ${
                    isOwn ? "justify-end" : ""
                  }`}
                >
                  {/* ✅ LEFT AVATAR */}
                  {!isOwn && (
                    <div className="h-12 w-12 rounded-3xs bg-neutrals-white-light flex flex-col items-center justify-center py-1 px-1 box-border">
                      <div className="flex items-center justify-center p-2.5">
                        <div className="relative font-semibold">
                          {initials}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ✅ MESSAGE BUBBLE */}
                  <div
                    className={`rounded-3xs flex items-end p-3 text-left text-sm ${
                      isOwn
                        ? "bg-primary-primary-500 !text-white"
                        : "bg-primary-primary-50 text-primary-primary-500"
                    }`}
                  >
                    <div className="w-full flex flex-col gap-3">
                      <div className="font-semibold">
                        {isOwn
                          ? "You"
                          : msg.senderRole === "lawyer"
                          ? "Lawyer"
                          : clientName || "Client"}
                      </div>

                      <div
                        className={`text-base ${
                          isOwn ? "text-white" : "text-neutrals-neutral-900"
                        }`}
                      >
                        {msg.content}
                      </div>

                      <div
                        className={`flex items-center justify-between gap-5 ${
                          isOwn ? "text-white" : "text-neutrals-neutral-900"
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          <Clock size={16} />
                          <span className="text-xs">
                            {formatTime(msg.timestamp || msg.createdAt)}
                          </span>
                        </div>

                        <div className="flex items-center gap-1">
                          <TickCircle size={16} />
                          <span className="text-xs">Seen</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ✅ RIGHT AVATAR */}
                  {isOwn && (
                    <div className="h-12 w-12 rounded-3xs bg-neutrals-white-light flex flex-col items-center justify-center py-1 px-1 box-border text-center text-base text-neutrals-neutral-900">
                      <div className="flex items-center justify-center p-2.5 shrink-0">
                        <div className="relative font-semibold">
                          {initials}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* ✅ Input */}
      <div className="self-stretch rounded-3xs bg-neutrals-white-light flex items-center justify-between p-6 gap-5 text-left text-base">
        <div className="flex items-center gap-3 w-full">
           {/*<Link21 size={20} />*/}
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Your message"
            className="flex-1 bg-transparent outline-none"
          />
        </div>

        <button onClick={sendMessage}>
          <Send2 size={20} />
        </button>
      </div>
    </div>
  );
};

export default Chat;
